import React, { useState, useRef, useEffect, useCallback } from "react";
import CrossCircleIcon from "../../../assets/svg/v1/cross-circle-icon";
import { SelectedChipsProps } from "../types";

const SelectedChips: React.FC<SelectedChipsProps> = ({
  selectedOptions,
  maxDisplayedSelections,
  onOptionToggle,
  containerRef,
  chipClassName,
  moreTagClassName,
  moreTagText = "more",
  autoFitChips = false,
}) => {
  const [computedMax, setComputedMax] = useState(selectedOptions.length);
  const measureRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const effectiveMax = autoFitChips ? computedMax : maxDisplayedSelections;
  const displayOptions = selectedOptions.slice(0, effectiveMax);
  const remainingCount = selectedOptions.length - effectiveMax;
  const hasRemainingCount = remainingCount > 0;

  const getAvailableWidth = () => {
    if (!containerRef.current) return 300;
    const containerWidth = containerRef.current.offsetWidth;
    const reservedSpace = hasRemainingCount ? 80 : 0;
    const paddingAndGaps = 16 + displayOptions.length * 8;
    return containerWidth - reservedSpace - paddingAndGaps;
  };

  const availableWidth = getAvailableWidth();
  const dynamicMaxWidth = autoFitChips
    ? undefined
    : Math.max(60, availableWidth / displayOptions.length);

  const baseChipClass = chipClassName
    ? `${chipClassName}${autoFitChips ? "" : " shrink-0"}`
    : null;

  const measureChipClass =
    baseChipClass ??
    "inline-flex items-center gap-1 px-[4px] py-1 text-xs rounded-[4px] shrink-0 border border-border-gray dark:border-border-gray-dk text-heading-text dark:text-heading-text-dk bg-card-highlight dark:bg-card-highlight-dk";

  const moreTag = hasRemainingCount ? (
    <div
      className={
        moreTagClassName ||
        "inline-flex items-center px-2 py-1 text-xs bg-card-highlight dark:bg-card-highlight-dk rounded-[4px] text-heading-text dark:text-heading-text-dk shrink-0"
      }
    >
      +{remainingCount} {moreTagText ?? "more"}
    </div>
  ) : null;

  const getChipClass = (option: (typeof selectedOptions)[0]) =>
    baseChipClass ??
    `inline-flex items-center gap-1 px-[4px] py-1 text-xs rounded-[4px] ${autoFitChips ? "shrink min-w-0" : "shrink-0"} border border-border-gray dark:border-border-gray-dk ${
      option?.bgColor ||
      "text-heading-text dark:text-heading-text-dk bg-card-highlight dark:bg-card-highlight-dk "
    }`;

  const renderChip = (option: (typeof selectedOptions)[0]) => (
    <div
      key={option.value}
      className={getChipClass(option)}
      style={dynamicMaxWidth ? { maxWidth: `${dynamicMaxWidth}px` } : undefined}
    >
      <span className="truncate">
        {option?.selectedOptionContent || option?.content}
      </span>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onOptionToggle(option);
        }}
        className="ml-1 hover:bg-hover-effect dark:hover:bg-hover-effect-dk rounded-[2px] p-0.5 shrink-0"
      >
        <CrossCircleIcon className="w-3 h-3 text-heading-text dark:text-heading-text-dk" />
      </button>
    </div>
  );

  const calculate = useCallback(() => {
    const measureRow = measureRef.current;
    const wrapper = wrapperRef.current;
    if (!measureRow || !wrapper) return;

    const availableWidth = wrapper.offsetWidth;
    if (availableWidth <= 0) {
      setComputedMax(1);
      return;
    }

    const gap = 4;
    const moreTagWidth = 72;
    const chipEls = Array.from(measureRow.children) as HTMLElement[];
    const totalChips = chipEls.length;

    let usedWidth = 0;
    let allFit = true;
    for (let i = 0; i < totalChips; i++) {
      const chipW = chipEls[i].offsetWidth;
      const added = i > 0 ? chipW + gap : chipW;
      if (usedWidth + added > availableWidth) {
        allFit = false;
        break;
      }
      usedWidth += added;
    }

    if (allFit) {
      setComputedMax(totalChips);
      return;
    }

    const budget = availableWidth - moreTagWidth - gap;
    usedWidth = 0;
    let fitCount = 0;
    for (let i = 0; i < totalChips; i++) {
      const chipW = chipEls[i].offsetWidth;
      const added = i > 0 ? chipW + gap : chipW;
      if (usedWidth + added > budget) break;
      usedWidth += added;
      fitCount++;
    }

    setComputedMax(Math.max(1, fitCount));
  }, []);

  useEffect(() => {
    if (!autoFitChips || selectedOptions.length === 0) return;

    requestAnimationFrame(calculate);

    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const ro = new ResizeObserver(calculate);
    ro.observe(wrapper);
    return () => ro.disconnect();
  }, [autoFitChips, selectedOptions, calculate]);

  return autoFitChips ? (
    <div ref={wrapperRef} className="flex-1 min-w-0 overflow-hidden relative">
      <div
        ref={measureRef}
        aria-hidden
        className="flex items-center gap-1 absolute pointer-events-none"
        style={{ visibility: "hidden", whiteSpace: "nowrap", top: 0, left: 0 }}
      >
        {selectedOptions.map((option) => (
          <div key={`m-${option.value}`} className={measureChipClass}>
            <span className="whitespace-nowrap">
              {option?.selectedOptionContent ||
                option?.content ||
                option?.label ||
                option?.value}
            </span>
            <span className="ml-1 p-0.5 shrink-0 inline-flex w-3 h-3" />
          </div>
        ))}
      </div>
      <div className="flex items-center gap-1 overflow-hidden">
        {displayOptions.map(renderChip)}
        {moreTag}
      </div>
    </div>
  ) : (
    <div className="flex items-center gap-1 flex-1 overflow-hidden">
      {displayOptions.map(renderChip)}
      {moreTag}
    </div>
  );
};

export default SelectedChips;
